import { useEffect, useState } from 'react';
import { getDeepLink } from '@binance/w3w-utils';
import { useBinanceDetection } from '../hooks/useBinanceDetection';

/**
 * Binance Wallet Button Component
 *
 * Displays "Open in Binance Wallet" button on mobile devices
 * when not already in Binance Wallet environment.
 *
 * Features:
 * - Mobile detection using react-responsive
 * - Binance environment detection
 * - Deep link generation for opening dApp in Binance Wallet
 */
export default function BinanceWalletButton() {
  const { shouldShowBinanceButton } = useBinanceDetection();
  const [deepLink, setDeepLink] = useState<string>('');

  useEffect(() => {
    // Only run on client side and when button should be shown
    if (typeof window === 'undefined' || !shouldShowBinanceButton) return;

    // Generate deep link for current page
    try {
      const currentUrl = window.location.href;
      const chainId = 56; // BSC (Binance Smart Chain)
      const { bnc, http } = getDeepLink(currentUrl, chainId);

      // Prefer bnc:// protocol link for better native app handling
      setDeepLink(bnc || http);
    } catch (error) {
      console.error('Failed to generate Binance deep link:', error);
    }
  }, [shouldShowBinanceButton]);

  // Don't render if conditions not met
  if (!shouldShowBinanceButton || !deepLink) {
    return null;
  }

  const handleClick = () => {
    if (deepLink) {
      window.location.href = deepLink;
    }
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-2 bg-[#F3BA2F] hover:bg-[#d9a520] text-white px-4 py-2 rounded-lg transition-colors font-medium shadow-sm"
      aria-label="Open in Binance Wallet"
    >
      {/* Binance Icon */}
      <svg
        className="w-5 h-5"
        viewBox="0 0 126.61 126.61"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g>
          <path d="M38.73 53.2l24.59-24.58 24.6 24.6 14.3-14.31L63.32 0 24.42 38.9l14.31 14.3zm-14.3 10.12L10.11 49.01 0 59.12l10.11 10.11 14.32-14.3zm48.89 0l24.6 24.6 14.31-14.31-24.6-24.6-14.31 14.3zM63.31 126.61l38.9-38.9-14.31-14.3-24.6 24.6-24.58-24.6-14.31 14.3 38.9 38.9z"/>
          <path d="M63.32 76.72l13.88-13.88-13.88-13.88-13.88 13.88z"/>
        </g>
      </svg>

      {/* Button Text */}
      <span className="text-sm whitespace-nowrap">
        Open in Binance Wallet
      </span>
    </button>
  );
}
